import React from "react";
import { AlertTriangle } from "lucide-react";

export default function CpmGantt({ schedule }) {
  if (!schedule) return null;
  const acts = schedule.activities || [];
  const total = schedule.project_duration || Math.max(...acts.map((a) => a.ef), 1);
  const ticks = Array.from({ length: Math.ceil(total / 5) + 1 }, (_, i) => i * 5);

  return (
    <div data-testid="cpm-gantt">
      <div className="flex flex-wrap items-center gap-4 mb-4">
        <Metric label="Project Duration" value={`${total} days`} />
        <Metric label="Activities" value={acts.length} />
        <Metric label="Critical Activities" value={(schedule.critical_path || []).length} accent="text-st-critical" />
        <div className="flex items-center gap-4 ml-auto text-[11px] uppercase tracking-wider font-semibold">
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-st-critical" /> Critical Path</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 bg-brand" /> Has Float</span>
        </div>
      </div>

      {/* axis */}
      <div className="flex border-b border-border pb-1 mb-1">
        <div className="w-56 shrink-0 text-[10px] uppercase tracking-wider text-ink-faint font-semibold">Activity</div>
        <div className="flex-1 relative h-4">
          {ticks.map((t) => (
            <span key={t} className="absolute text-[10px] font-mono text-ink-faint -translate-x-1/2" style={{ left: `${(t / total) * 100}%` }}>
              {t}d
            </span>
          ))}
        </div>
      </div>

      <div className="space-y-1">
        {acts.map((a) => {
          const left = (a.es / total) * 100;
          const width = Math.max((a.duration / total) * 100, 2);
          const crit = a.critical;
          return (
            <div key={a.id} className="flex items-center group" data-testid={`gantt-row-${a.id}`}>
              <div className="w-56 shrink-0 pr-2">
                <div className="flex items-center gap-1.5">
                  {crit && <span className="w-1.5 h-1.5 bg-st-critical shrink-0" />}
                  <span className={`text-xs font-medium truncate ${crit ? "text-ink" : "text-ink-muted"}`}>{a.name}</span>
                </div>
                <div className="text-[10px] font-mono text-ink-faint pl-3">{a.trade} · {a.duration}d · float {a.slack}d</div>
              </div>
              <div className="flex-1 relative h-7 bg-surface-muted">
                {ticks.map((t) => (
                  <span key={t} className="absolute top-0 bottom-0 w-px bg-border" style={{ left: `${(t / total) * 100}%` }} />
                ))}
                <div
                  className={`absolute top-1 bottom-1 ${crit ? "bg-st-critical" : "bg-brand"} flex items-center px-1.5 transition-all group-hover:brightness-110`}
                  style={{ left: `${left}%`, width: `${width}%` }}
                  title={`${a.name}: ES ${a.es} → EF ${a.ef} (LS ${a.ls}, LF ${a.lf})`}
                >
                  <span className="text-[10px] font-mono text-white font-semibold truncate">{a.id}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {(schedule.critical_path || []).length > 0 && (
        <div className="mt-4 flex items-start gap-2 bg-st-critical/5 border-l-4 border-st-critical p-3">
          <AlertTriangle className="w-4 h-4 text-st-critical mt-0.5 shrink-0" />
          <div className="text-xs text-ink">
            <span className="font-semibold">Critical path ({total} days): </span>
            <span className="font-mono">{schedule.critical_path.join(" → ")}</span>
            <span className="text-ink-muted"> — any delay here pushes the entire project completion.</span>
          </div>
        </div>
      )}
    </div>
  );
}

function Metric({ label, value, accent }) {
  return (
    <div className="bg-white border border-border px-3 py-1.5">
      <div className="text-[10px] uppercase tracking-wider text-ink-faint font-semibold">{label}</div>
      <div className={`font-heading text-lg font-bold ${accent || "text-ink"}`}>{value}</div>
    </div>
  );
}
